import {
  Box,
  Button,
  Container,
  FormField,
  Header,
  Input,
  Select,
  SelectProps,
  SpaceBetween,
  StatusIndicator,
  Table,
  TableProps,
  Textarea,
} from "@amzn/awsui-components-console";
import React, { useMemo, useState } from "react";

import useAdminAgentConfig from "@/hooks/admin/useAdminAgentConfig";
import useModal from "@/hooks/useModal";
import { formatTimestamp } from "@/utils/utils";

import EmptyState from "../EmptyState";
import { SEASON_FORMATS, SportSeason } from "./types";

const FORMAT_OPTIONS: SelectProps.Option[] = SEASON_FORMATS.map((f) => ({
  label: f,
  value: f,
}));

const LastChanged: React.FC<{ by: string | null; at: string | null }> = ({ by, at }) => {
  if (!by) {
    return <Box color="text-status-inactive">—</Box>;
  }
  return (
    <SpaceBetween size="xxs">
      <Box>{by}</Box>
      {at && (
        <Box color="text-status-inactive" fontSize="body-s">
          {formatTimestamp(at)}
        </Box>
      )}
    </SpaceBetween>
  );
};

const AgentConfiguration: React.FC = () => {
  const {
    model,
    sports,
    chatStatus,
    availableModels,
    loading,
    loadConfig,
    setActiveModel,
    setChatEnabled,
    upsertSport,
    deleteSport,
  } = useAdminAgentConfig();

  // --- Chat kill switch (andon cord) ---
  const [savingChat, setSavingChat] = useState(false);
  const [disableReason, setDisableReason] = useState("");

  // Default-on: treat an unloaded/absent status as enabled so the control
  // never implies chat is down while the page is still loading.
  const chatEnabled = chatStatus?.enabled ?? true;

  const { toggle: toggleChatConfirm, ModalComponent: ChatConfirmModal } = useModal({
    title: chatEnabled ? "Disable chat?" : "Enable chat?",
    confirmLabel: chatEnabled ? "Disable chat" : "Enable chat",
    loading: savingChat,
    content: chatEnabled ? (
      <SpaceBetween size="s">
        <Box>
          This pulls the andon cord: MAMBA stops accepting new chat messages and
          users see a maintenance message instead. It takes effect within about a
          minute. In-flight messages already queued may still be answered.
        </Box>
        <FormField
          label="Reason (optional)"
          description="Shown here for the next responder; recorded with your identity."
        >
          <Textarea
            value={disableReason}
            placeholder="e.g., INCIDENT-1234: investigating prompt-injection reports"
            onChange={({ detail }) => setDisableReason(detail.value)}
          />
        </FormField>
      </SpaceBetween>
    ) : (
      <Box>
        Re-enable chat for all MAMBA users. New messages will be accepted again
        within about a minute.
      </Box>
    ),
    onConfirm: async () => {
      setSavingChat(true);
      try {
        // Rethrows on failure so the dialog stays open for retry.
        if (chatEnabled) {
          await setChatEnabled(false, disableReason);
        } else {
          await setChatEnabled(true);
        }
        setDisableReason("");
      } finally {
        setSavingChat(false);
      }
    },
  });

  // --- Active model change ---
  const [pendingModel, setPendingModel] = useState<SelectProps.Option | null>(null);
  const [savingModel, setSavingModel] = useState(false);

  const modelOptions = useMemo<SelectProps.Option[]>(
    () =>
      availableModels.map((m) => ({
        label: m.modelName,
        value: m.modelId,
        description: m.provider,
      })),
    [availableModels]
  );

  const { toggle: toggleConfirm, ModalComponent: ConfirmModal } = useModal({
    title: "Confirm model change",
    confirmLabel: "Confirm change",
    loading: savingModel,
    content: (
      <SpaceBetween size="s">
        <Box>
          You&apos;re about to change the active model for all MAMBA users. It takes
          effect within about a minute.
        </Box>
        <Box>
          <strong>{model?.modelName ?? "current model"}</strong>
          {" → "}
          <strong>{pendingModel?.label}</strong>
        </Box>
      </SpaceBetween>
    ),
    onConfirm: async () => {
      if (!pendingModel?.value) return;
      setSavingModel(true);
      try {
        await setActiveModel(pendingModel.value, pendingModel.label ?? pendingModel.value);
        setPendingModel(null);
      } finally {
        setSavingModel(false);
      }
    },
  });

  const modelChanged = !!pendingModel?.value && pendingModel.value !== model?.modelId;

  // --- Season add / edit ---
  const [isAdding, setIsAdding] = useState(false);
  const [editorSport, setEditorSport] = useState("");
  const [editorSeason, setEditorSeason] = useState("");
  const [editorFormat, setEditorFormat] = useState<SelectProps.Option>(FORMAT_OPTIONS[0]);
  const [savingSport, setSavingSport] = useState(false);

  const editorInvalid = !editorSport.trim() || !editorSeason.trim();

  const { toggle: toggleEditor, ModalComponent: EditorModal } = useModal({
    title: isAdding ? "Add sport" : `Edit ${editorSport} season`,
    confirmLabel: "Save",
    loading: savingSport,
    disabled: editorInvalid,
    content: (
      <SpaceBetween size="m">
        <FormField label="Sport">
          <Input
            value={editorSport}
            disabled={!isAdding}
            placeholder="e.g., NFL"
            onChange={({ detail }) => setEditorSport(detail.value)}
          />
        </FormField>
        <FormField label="Current season">
          <Input
            value={editorSeason}
            placeholder="e.g., 2025-26"
            onChange={({ detail }) => setEditorSeason(detail.value)}
          />
        </FormField>
        <FormField
          label="Format"
          description="How the season value is written (used to derive the previous season)."
        >
          <Select
            selectedOption={editorFormat}
            options={FORMAT_OPTIONS}
            onChange={({ detail }) => setEditorFormat(detail.selectedOption)}
          />
        </FormField>
      </SpaceBetween>
    ),
    onConfirm: async () => {
      setSavingSport(true);
      try {
        // Rethrows on failure so the dialog stays open for retry.
        await upsertSport(
          editorSport.trim(),
          editorSeason.trim(),
          editorFormat.value ?? SEASON_FORMATS[0]
        );
      } finally {
        setSavingSport(false);
      }
    },
  });

  const openAddSport = () => {
    setIsAdding(true);
    setEditorSport("");
    setEditorSeason("");
    setEditorFormat(FORMAT_OPTIONS[0]);
    toggleEditor();
  };

  const openEditSport = (s: SportSeason) => {
    setIsAdding(false);
    setEditorSport(s.sport);
    setEditorSeason(s.currentSeason);
    setEditorFormat(FORMAT_OPTIONS.find((o) => o.value === s.format) ?? FORMAT_OPTIONS[0]);
    toggleEditor();
  };

  // --- Season delete ---
  const [deleteTarget, setDeleteTarget] = useState<SportSeason | null>(null);
  const [deletingSport, setDeletingSport] = useState(false);

  const { toggle: toggleDeleteConfirm, ModalComponent: DeleteConfirmModal } = useModal({
    title: `Delete ${deleteTarget?.sport ?? "sport"}?`,
    confirmLabel: "Delete",
    loading: deletingSport,
    content: (
      <Box>
        Remove the season context for <strong>{deleteTarget?.sport}</strong>? The agent
        will no longer treat any season as current for this sport. This can&apos;t be
        undone, but you can add the sport again later.
      </Box>
    ),
    onConfirm: async () => {
      if (!deleteTarget) return;
      setDeletingSport(true);
      try {
        // Rethrows on failure so the dialog stays open for retry.
        await deleteSport(deleteTarget.sport);
        setDeleteTarget(null);
      } finally {
        setDeletingSport(false);
      }
    },
  });

  const openDeleteSport = (s: SportSeason) => {
    setDeleteTarget(s);
    toggleDeleteConfirm();
  };

  // Alphabetical by sport so a reader can find a sport by name. Sorting a copy
  // because `sports` is the hook's state and `Array.prototype.sort` is in-place.
  const orderedSports = useMemo(
    () => [...sports].sort((a, b) => a.sport.localeCompare(b.sport)),
    [sports]
  );

  const columnDefinitions = useMemo<TableProps<SportSeason>["columnDefinitions"]>(
    () => [
      { id: "sport", header: "Sport", cell: (s) => <Box variant="strong">{s.sport}</Box> },
      { id: "currentSeason", header: "Current season", cell: (s) => s.currentSeason },
      { id: "format", header: "Format", cell: (s) => s.format },
      {
        id: "lastChangedBy",
        header: "Last changed by",
        cell: (s) => <LastChanged by={s.lastChangedBy} at={s.lastChangedAt} />,
      },
      {
        id: "actions",
        header: "Actions",
        cell: (s) => (
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              ariaLabel={`Edit ${s.sport} season`}
              iconName="edit"
              variant="inline-link"
              onClick={() => openEditSport(s)}
            >
              Edit
            </Button>
            <Button
              ariaLabel={`Delete ${s.sport} season`}
              iconName="remove"
              variant="inline-link"
              onClick={() => openDeleteSport(s)}
            >
              Delete
            </Button>
          </SpaceBetween>
        ),
      },
    ],
    []
  );

  return (
    <SpaceBetween size="l">
      <Container
        data-testid="active-model-card"
        header={<Header variant="h2">Active model</Header>}
      >
        <SpaceBetween size="m">
          <Box>
            Select which model powers MAMBA. Only tool-use-capable Anthropic
            (Claude) and Amazon (Nova) models are shown.
          </Box>
          <div data-testid="current-model">
            <SpaceBetween size="xxs">
              <StatusIndicator type="success">
                {model?.modelName ?? "Loading…"}
              </StatusIndicator>
              {model?.modelId && (
                <Box color="text-status-inactive" fontSize="body-s">
                  {model.modelId}
                </Box>
              )}
              <LastChanged by={model?.lastChangedBy ?? null} at={model?.lastChangedAt ?? null} />
            </SpaceBetween>
          </div>
          <FormField label="Switch model">
            <Select
              data-testid="model-select"
              selectedOption={pendingModel}
              options={modelOptions}
              placeholder="Select a different model…"
              empty="No selectable models found"
              onChange={({ detail }) => setPendingModel(detail.selectedOption)}
            />
          </FormField>
          <Box>
            <Button
              variant="primary"
              disabled={!modelChanged}
              onClick={toggleConfirm}
            >
              Save model change
            </Button>
          </Box>
        </SpaceBetween>
      </Container>

      <Table
        {...({ "data-testid": "season-context-table" } as object)}
        variant="container"
        columnDefinitions={columnDefinitions}
        items={orderedSports}
        loading={loading}
        loadingText="Loading season context"
        trackBy="sport"
        empty={<EmptyState title="No sports configured" />}
        header={
          <Header
            counter={`(${sports.length})`}
            description="The season the agent treats as current for each sport."
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button iconName="refresh" onClick={() => loadConfig()}>
                  Refresh
                </Button>
                <Button iconName="add-plus" onClick={openAddSport}>
                  Add sport
                </Button>
              </SpaceBetween>
            }
          >
            Season context
          </Header>
        }
      />

      <Container
        data-testid="chat-availability-card"
        header={<Header variant="h2">Chat availability</Header>}
      >
        <SpaceBetween size="m">
          <Box>
            The chat kill switch (andon cord). Disable to immediately stop MAMBA
            from accepting new chat messages during an incident; users see a
            maintenance message. Re-enable to resume.
          </Box>
          <div data-testid="chat-status">
            <SpaceBetween size="xxs">
              <StatusIndicator type={chatEnabled ? "success" : "stopped"}>
                {chatStatus ? (chatEnabled ? "Chat enabled" : "Chat disabled") : "Loading…"}
              </StatusIndicator>
              {!chatEnabled && chatStatus?.reason && (
                <Box color="text-status-inactive" fontSize="body-s">
                  Reason: {chatStatus.reason}
                </Box>
              )}
              <LastChanged
                by={chatStatus?.lastChangedBy ?? null}
                at={chatStatus?.lastChangedAt ?? null}
              />
            </SpaceBetween>
          </div>
          <Box>
            <Button
              data-testid="chat-toggle"
              variant={chatEnabled ? "normal" : "primary"}
              loading={savingChat}
              // Kill switch: don't let an admin act on the default-on
              // placeholder before the real status loads.
              disabled={loading || !chatStatus}
              onClick={() => {
                setDisableReason("");
                toggleChatConfirm();
              }}
            >
              {chatEnabled ? "Disable chat" : "Enable chat"}
            </Button>
          </Box>
        </SpaceBetween>
      </Container>

      <ChatConfirmModal />
      <ConfirmModal />
      <EditorModal />
      <DeleteConfirmModal />
    </SpaceBetween>
  );
};

export default AgentConfiguration;
